import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SalesCallData, SalesCoachingFilters } from "@/modules/salesCoaching/features/SalesCallScoring/types";

export interface SalesCoachingState {
  data: SalesCallData[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  pagination: {
    pageIndex: number;
    pageSize: number;
  };
  filters: SalesCoachingFilters;
  sorting: {
    id: string;
    desc: boolean;
  }[];
  selectedCall: SalesCallData | null;
}

const initialState: SalesCoachingState = {
  data: [],
  loading: false,
  error: null,
  totalCount: 0,
  pagination: {
    pageIndex: 0,
    pageSize: 25,
  },
  filters: {},
  sorting: [{ id: 'date', desc: true }], // Default: newest first
  selectedCall: null,
};

const salesCoachingSlice = createSlice({
  name: "salesCoaching",
  initialState,
  reducers: {
    setSalesCoachingData: (state, action: PayloadAction<{ data: SalesCallData[]; totalCount: number }>) => {
      state.data = action.payload.data;
      state.totalCount = action.payload.totalCount;
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setPagination: (state, action: PayloadAction<{ pageIndex: number; pageSize: number }>) => {
      state.pagination = action.payload;
    },
    setFilters: (state, action: PayloadAction<SalesCoachingFilters>) => {
      state.filters = action.payload;
      state.pagination.pageIndex = 0; // Reset to first page when filters change
    },
    updateFilter: (state, action: PayloadAction<{ key: keyof SalesCoachingFilters; value: any }>) => {
      state.filters = {
        ...state.filters,
        [action.payload.key]: action.payload.value,
      };
      state.pagination.pageIndex = 0; // Reset to first page when filters change
    },
    setSorting: (state, action: PayloadAction<{ id: string; desc: boolean }[]>) => {
      state.sorting = action.payload;
    },
    resetFilters: (state) => {
      state.filters = {};
      state.pagination.pageIndex = 0;
    },
    setSelectedCall: (state, action: PayloadAction<SalesCallData | null>) => {
      state.selectedCall = action.payload;
    },
    updateCallManagerNotes: (state, action: PayloadAction<{ callId: string; notes: string }>) => {
      const call = state.data.find(c => c.callId === action.payload.callId);
      if (call) {
        call.managerNotes = action.payload.notes;
      }
      if (state.selectedCall?.callId === action.payload.callId) {
        state.selectedCall.managerNotes = action.payload.notes;
      }
    },
  },
});

export const {
  setSalesCoachingData,
  setLoading,
  setError,
  setPagination,
  setFilters,
  updateFilter,
  setSorting,
  resetFilters,
  setSelectedCall,
  updateCallManagerNotes,
} = salesCoachingSlice.actions;

export default salesCoachingSlice.reducer;